import React, { useEffect, useState } from "react";
import RecentTracker from "@/components/RecentTracker";
import { RecordSwitcher } from "@/components/RecordSwitcher";
import { OpsStatusPill } from "./Projects";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusPill from "../components/StatusPill";
import FrsTable from "../components/FrsTable";
import FrsSnapshotsDialog from "../components/FrsSnapshotsDialog";
import FrsHistoryDrawer from "../components/FrsHistoryDrawer";
import MobilizationsList from "../components/MobilizationsList";
import ProjectInfoCard from "../components/ProjectInfoCard";
import ProjectLaborPanel from "../components/ProjectLaborPanel";
import ScopeRatesPanel from "../components/ScopeRatesPanel";
import DepConflictsBar from "../components/DepConflictsBar";
import RateDriftBar from "../components/RateDriftBar";
import GcConfirmPanel from "../components/GcConfirmPanel";
import ProjectIssuesPanel from "../components/ProjectIssuesPanel";
import ProjectSchedulesSection from "../components/ProjectSchedulesSection";
import MaterialTrackingView from "../components/MaterialTrackingView";
import OnsiteReviewPanel from "../components/OnsiteReviewPanel";
import { drawingUrl } from "../../drawings/openDrawing";
import NeedsReview from "../components/NeedsReview";
import QuickDispatchButton from "../components/QuickDispatchButton";
import ProjectNotesTab from "../components/ProjectNotesTab";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";

const RA_TAB = "rounded-none border-b-2 border-transparent bg-transparent px-3 py-2 text-sm font-medium text-slate-500 shadow-none hover:text-slate-800 data-[state=active]:border-[#007BFF] data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-[#007BFF] data-[state=active]:shadow-none";

export default function ProjectDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "frs";
  const [project, setProject] = useState(null);
  const [frsKey, setFrsKey] = useState(0);
  const [jobs, setJobs] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setJobs(r.data || [])).catch(() => {}); }, []);   // FT-4 — remount the grid after Shift successors
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => {});
  }, [id]);

  // DRW-LINKS — anything still pointing at the old in-RA drawings tab (a bookmark, an old link in
  // a digest) goes straight to the sheet it asked for instead of an interstitial.
  useEffect(() => {
    if (tab !== "drawings") return;
    navigate(drawingUrl({ project: id, sheet: params.get("sheet") || "", room: params.get("room") || "",
                          product: params.get("product") || "", from: params.get("from") || "ra",
                          vs: params.get("vs") || "", back: true }), { replace: true });
    // eslint-disable-next-line
  }, [tab]);

  if (!project) return <div className="px-6 py-10 text-sm text-gray-500">Loading…</div>;

  return (
    <div>
      <RecentTracker kind="ra_job" refId={project.id} label={project.project_name}
                     sub={project.job_number ? `RA · ${project.job_number}` : "RA"} path={`/projects/${project.id}`} />
      <div className="mb-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5" data-testid="ra-project-header">
        <div className="flex items-center gap-4">
          <div className="min-w-0">
            <RecordSwitcher variant="title" currentId={project.id} testid="ra-job-switcher" empty="No jobs match."
                            currentLabel={`${project.job_number ? `${project.job_number} ` : ""}${project.project_name}`}
                            items={jobs.map((j) => ({ id: j.id, label: `${j.job_number ? `${j.job_number} ` : ""}${j.project_name}` }))}
                            onPick={(v) => navigate(`/projects/${v}?tab=${tab}`)} />
            <div className="mt-0.5 text-[11px] leading-tight text-slate-500" data-testid="ra-project-location">
              {[project.address, project.city, project.state].filter(Boolean).join(", ") || "No address yet"}
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <QuickDispatchButton projectId={project.id} />
            <OpsStatusPill value={project.ops_status} testId="ra-project-status" />
            <span className="text-sm font-semibold tabular-nums text-slate-900" data-testid="ra-project-hours">
              {Number(project.budgeted_hours || 0).toLocaleString()} hrs
            </span>
          </div>
        </div>
      </div>

      <div className="mb-3"><NeedsReview projectId={project.id} /></div>
      <Tabs value={tab} onValueChange={(v) => setParams((cur) => {
        const next = new URLSearchParams(cur);
        next.set("tab", v);
        if (v !== "material") next.delete("ticket");
        return next;
      }, { replace: true })} className="w-full">
        <div className="flex items-start justify-between gap-2">
        <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1 rounded-none bg-transparent p-0" data-testid="ra-tabs">
          <TabsTrigger value="frs" className={RA_TAB} data-testid="tab-frs">FRS</TabsTrigger>
          <TabsTrigger value="mobilizations" className={RA_TAB} data-testid="tab-mobilizations">Mobilizations</TabsTrigger>
          <TabsTrigger value="schedules" className={RA_TAB} data-testid="tab-schedules">Schedules</TabsTrigger>
          <TabsTrigger value="material" className={RA_TAB} data-testid="tab-material">Material</TabsTrigger>
          <TabsTrigger value="review" className={RA_TAB} data-testid="tab-review">Material Review</TabsTrigger>
          <TabsTrigger value="info" className={RA_TAB} data-testid="tab-info">Info</TabsTrigger>
          {/* DRW-LINKS — Drawings is its own tab now, so this is a way THROUGH, not a tab to sit
              on. No interstitial: it opens the sheet set itself. */}
          <button type="button" data-testid="tab-drawings"
                  onClick={() => navigate(drawingUrl({ project: project.id, from: "ra", back: true }))}
                  className="inline-flex items-center gap-1 whitespace-nowrap border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-800">
            Drawings
          </button>
          <TabsTrigger value="notes" className={RA_TAB} data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="history" className={RA_TAB} data-testid="tab-history">History</TabsTrigger>
        </TabsList>
        </div>

        <div className="mt-4">
          <TabsContent value="frs">
            <div className="space-y-2">
              <DepConflictsBar projectId={project.id} onShifted={() => setFrsKey((k) => k + 1)} />
              <RateDriftBar projectId={project.id} />
              <FrsTable key={frsKey} projectId={project.id} autoRequestScope={params.get("mobScope") || ""}
                        onAutoRequestOpened={() => setParams((cur) => { const n = new URLSearchParams(cur); n.delete("mobScope"); return n; }, { replace: true })} />
            </div>
          </TabsContent>
          <TabsContent value="mobilizations">
            <div className="space-y-2">
              <GcConfirmPanel projectId={project.id} />
              <MobilizationsList projectId={project.id} />
            </div>
          </TabsContent>
          <TabsContent value="schedules">
            <ProjectSchedulesSection project={project} />
          </TabsContent>
          <TabsContent value="material">
            <MaterialTrackingView projectId={project.id}
                                  focusTicket={params.get("ticket") || ""} />
          </TabsContent>
          <TabsContent value="review">
            <div className="text-xs text-gray-600 mb-2">Deleted-from-Scope requests from the field. Nothing here changes the FRS until a decision is confirmed.</div>
            <OnsiteReviewPanel mode="review" projectId={project.id} actor={user?.email || user?.name || ""} />
          </TabsContent>
          <TabsContent value="notes">
            <ProjectNotesTab projectId={project.id} />
          </TabsContent>
          <TabsContent value="history">
            <div className="space-y-4">
              <FrsHistoryDrawer inline projectId={project.id}
                                onView={(v) => v && navigate(`/projects/${project.id}?tab=frs&frsVersion=${v.id}&frsMode=${v.mode}&frsLabel=${encodeURIComponent(v.label)}`)}
                                onRestored={() => setFrsKey((k) => k + 1)} />
              <FrsSnapshotsDialog inline projectId={project.id} onRestored={() => setFrsKey((k) => k + 1)} />
            </div>
          </TabsContent>
          <TabsContent value="info">
            <div className="space-y-4">
              <ProjectInfoCard project={project} onUpdated={setProject} />
              <ProjectLaborPanel project={project} onUpdated={setProject} />
              <ScopeRatesPanel projectId={project.id} />
              <ProjectIssuesPanel projectId={project.id} />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
